import { useQuery } from '@apollo/client';
import { Grid, Icon, Pagination, Paper } from '@mui/material';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ENTITLEMENTS_QUERY from 'gql/queries/PermissionProvider/getEntitlements.gql';

import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { EntitlementItem } from 'app/components/RichList/items/PermissionProvider/EntitlementItem';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import '../styles/PermissionsManagement.scss';

export function EntitlementList() {
  const entitlementState = useState('');

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const entitlementsQuery = useQuery<any, any>(ENTITLEMENTS_QUERY, { fetchPolicy: 'network-only' });
  const { refetch } = entitlementsQuery;

  const handlePagination = (event, page: number) => {
    const offset = (page - 1) * 10;
    refetch({ offset: offset });
  };

  return (
    <GQLGuard
      query={entitlementsQuery}
      onError={error => {
        dispatch(
          AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {data => (
        <>
          <RichList
            onFilterChange={refetch}
            filters={[
              {
                field: 'entitlement',
                label: t(translations.pages.managePermissionsSettings.entitlement()),
                stateHandler: entitlementState
              }
            ]}
            staticHeading={
              <Grid
                container
                className="permission-settings__list-heading"
                component={Paper}
                square
              >
                <Grid item md={1} className="permission-settings__list-heading__item">
                  {t(translations.common.id())}
                </Grid>
                <Grid item md={5} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.entitlement())}
                </Grid>
                <Grid item md={5} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.roles())}
                </Grid>
                <Grid item md={1} className="permission-settings__list-heading__item">
                  {t(translations.pages.managePermissionsSettings.actions())}
                </Grid>
              </Grid>
            }
          >
            {data.entitlements.length === 0 ? (
              <NoItems
                icon={<Icon className="fas fa-info-circle" />}
                message={t(translations.pages.managePermissionsSettings.noItemFound())}
              />
            ) : (
              <Grid container className="permission-settings__list" component={Paper}>
                {data.entitlements.map(ent => (
                  <ErrorBoundary key={ent.id} boundaryTag="ientitlementItemBoundary">
                    <EntitlementItem entitlement={ent} key={ent.id} />
                  </ErrorBoundary>
                ))}
              </Grid>
            )}
          </RichList>
          {Math.floor(data.entitlementsCount / 10) > 0 && (
            <Grid container className="permission-settings__pagination">
              <Pagination
                count={Math.ceil(data.entitlementsCount / 10)}
                onChange={handlePagination}
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
                size="medium"
                color="primary"
                siblingCount={2}
                boundaryCount={2}
              />
            </Grid>
          )}
        </>
      )}
    </GQLGuard>
  );
}
